import type { InventoryLocation } from "../config";
import type { StockAnswer } from "../stock/sheet";

export type Driver = { id: number; name: string; phone?: string | null };
export type User = {
  id: number;
  name: string;
  description: string;
  phone: string | null;
  isDriver: boolean;
  isAdmin: boolean;
  isOrderDriver: boolean; // admins are hidden from the read-only (viewer) PIN
  active: boolean;
};
export type UserInput = { name: string; description: string; phone: string; isDriver: boolean; isAdmin: boolean; isOrderDriver?: boolean; active?: boolean };
export type Company = { id: number; name: string; balance: number };
export type Direction = "in" | "out";
export type Movement = {
  id: number;
  direction: Direction;
  count: number;
  driverName: string;
  companyName: string;
  companyId: number;
  createdAt: string;
};

export type Task = {
  id: number;
  dueAt: string;
  subject: string;
  description: string;
  timing: Timing | null;
  createdBy: string | null;
  driverId: number | null;
  driverName: string | null;
  done: boolean;
  doneAt: string | null;
  createdAt: string;
  photoIds: number[];
  smsSent: boolean;
};
export type Timing = "before" | "at" | "after";
export const TIMING_LABELS: Record<Timing, string> = { before: "Before", at: "At delivery", after: "After" };
export type TaskInput = {
  dueAt: string;
  subject: string;
  description: string;
  timing: Timing | null;
  createdBy: string | null;
  driverId: number | null;
  smsTo?: string; // E.164; when set the server texts this number about the new task
};
export type CreatedTask = Task & { sms?: { ok: boolean; to?: string; error?: string } };
export type TaskPatch = Partial<TaskInput & { done: boolean }>;
export type DriverLocation = {
  driverId: number;
  name: string;
  lat: number;
  lng: number;
  accuracy: number | null;
  updatedAt: string;
};
export type SmsMessage = {
  id: number;
  to: string;
  sender: string;
  body: string;
  taskId: number | null;
  status: string;
  error: string | null;
  segments: number | null;
  costNok: number;
  costEstimated: boolean;
  createdAt: string;
};

export { INVENTORY_LOCATIONS, inventoryLocationLabel, isInventoryLocation, type InventoryLocation } from "../config";
export type InventoryItem = {
  id: number;
  description: string;
  itemType: string;
  brand: string;
  model: string;
  tags: string[];
  location: InventoryLocation;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  photoIds: number[];
};
export type InventoryInput = Pick<InventoryItem, "description" | "itemType" | "brand" | "model" | "tags" | "location">;
/** What the AI suggests — never the location. */
/** What the last AI call cost: resolved model id, wall time, and OpenRouter's charge. */
export type AiMeta = { model: string; ms: number; costUsd: number | null; costNok: number | null };
export type InventorySuggestion = Omit<InventoryInput, "location"> & { meta?: AiMeta };
export function formatAiMeta(m: AiMeta) {
  const model = m.model.replace(/^~/, "");
  const time = m.ms >= 10_000 ? `${Math.round(m.ms / 1000)} s` : `${(m.ms / 1000).toFixed(1)} s`;
  const cost =
    m.costNok === null ? (m.costUsd === null ? "n/a" : `$${m.costUsd.toFixed(4)}`) : `${m.costNok.toFixed(m.costNok < 0.01 ? 4 : 2)} NOK`;
  return `AI model: ${model}, time: ${time}, cost: ${cost}`;
}
export type AiModel = "deepseek" | "glm" | "other";
export const AI_MODEL_LABELS: Record<AiModel, { name: string; detail: string }> = {
  deepseek: { name: "DeepSeek", detail: "deepseek-flash, v4.1 (default)" },
  glm: { name: "GLM", detail: "z-ai glm-flash" },
  other: { name: "Qwen", detail: "qwen3.8-flash" },
};
export type InventoryPage = { items: InventoryItem[]; total: number; pageSize: number };

export type SmsStats = {
  balance: { amount: number; currency: string; nok: number | null } | null;
  weeks: Array<{
    weekStart: string;
    sent: number;
    failed: number;
    segments: number;
    costNok: number;
    costEstimated: boolean;
  }>;
};

/** "viewer" = the read-only PIN: sees the Scheduler, cannot change anything. */
export type Role = "full" | "viewer";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  // Access cookie missing or stale (PIN changed): show the PIN gate again.
  if (res.status === 401 && !path.startsWith("/api/verify-pin")) window.dispatchEvent(new Event("auth-required"));
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

const withBody = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});
const post = (body: unknown) => withBody("POST", body);

export const api = {
  drivers: () => request<Driver[]>("/api/drivers"),
  verifyPin: (pin: string) => request<{ ok: true; role: Role }>("/api/verify-pin", post({ pin })),
  auth: () => request<{ ok: boolean; role: Role | null }>("/api/auth"),
  logout: () => request<{ ok: true }>("/api/auth", { method: "DELETE" }),
  adminSmsStats: (pin: string) => request<SmsStats>("/api/admin/sms-stats", post({ pin })),
  adminBroadcastSms: (text: string, pin: string, numbers: string[]) =>
    request<{ sent: number; failed: number; skipped: number; results: Array<{ name: string; to: string; ok: boolean; error: string | null }> }>(
      "/api/admin/broadcast-sms",
      post({ text, pin, numbers }),
    ),
  createDriver: (name: string, pin: string) => request<Driver>("/api/drivers", post({ name, pin })),
  users: () => request<User[]>("/api/users"),
  createUser: (input: UserInput, pin: string) => request<User>("/api/users", post({ ...input, pin })),
  updateUser: (id: number, input: Partial<UserInput>, pin: string) =>
    request<User>(`/api/users/${id}`, withBody("PATCH", { ...input, pin })),
  companies: () => request<Company[]>("/api/companies"),
  createCompany: (name: string) => request<Company>("/api/companies", post({ name })),
  movements: (params: { companyId?: number; beforeId?: number; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.companyId) search.set("companyId", String(params.companyId));
    if (params.beforeId) search.set("beforeId", String(params.beforeId));
    if (params.limit) search.set("limit", String(params.limit));
    const qs = search.toString();
    return request<Movement[]>(`/api/movements${qs ? `?${qs}` : ""}`);
  },
  register: (input: { driverId: number; companyId: number; direction: Direction; count: number }) =>
    request<{ id: number; newBalance: number }>("/api/movements", post(input)),
  undo: (id: number, pin?: string) =>
    request<{ ok: true; newBalance: number }>(`/api/movements/${id}`, {
      method: "DELETE",
      ...(pin !== undefined && {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      }),
    }),

  sendLocation: (driverId: number, pos: { lat: number; lng: number; accuracy: number | null }) =>
    request<{ ok: true }>("/api/location", post({ driverId, ...pos })),
  clearLocation: (driverId: number) => request<{ ok: true }>("/api/location", withBody("DELETE", { driverId })),
  locations: () => request<DriverLocation[]>("/api/locations"),
  version: () => request<{ version: string; since: string | null }>("/api/version"),
  tasks: () => request<Task[]>("/api/tasks"),
  createTask: (input: TaskInput) => request<CreatedTask>("/api/tasks", post(input)),
  updateTask: (id: number, patch: TaskPatch) => request<Task>(`/api/tasks/${id}`, withBody("PATCH", patch)),
  deleteTask: (id: number, pin: string) =>
    request<{ ok: true }>(`/api/tasks/${id}`, { method: "DELETE", headers: { "X-Admin-Pin": pin } }),
  sendTaskSms: (taskId: number, to: string, text: string) =>
    request<{ ok: true; to: string }>(`/api/tasks/${taskId}/sms`, post({ to, text })),
  uploadPhoto: (taskId: number, photo: Blob) =>
    request<{ id: number }>(`/api/tasks/${taskId}/photos`, {
      method: "POST",
      headers: { "Content-Type": photo.type || "image/jpeg" },
      body: photo,
    }),
  deletePhoto: (id: number) => request<{ ok: true }>(`/api/photos/${id}`, { method: "DELETE" }),
  photoUrl: (id: number) => `/api/photos/${id}`,

  // The Inventory tool: the stock sheet worked out; `fresh` reads the sheet again.
  stock: (fresh = false) => request<StockAnswer>(`/api/stock${fresh ? "?fresh=1" : ""}`),
  // A shop's requested amount changed; the answer is the stock as it is now.
  stockRequest: (body: { shop: string; week: string; itemId: string; amount: number }) => request<StockAnswer>("/api/stock/request", post(body)),
  inventory: (params: { q?: string; location?: InventoryLocation; beforeId?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.location) search.set("location", params.location);
    if (params.beforeId) search.set("beforeId", String(params.beforeId));
    const qs = search.toString();
    return request<InventoryPage>(`/api/inventory${qs ? `?${qs}` : ""}`);
  },
  createInventoryItem: (input: InventoryInput, createdBy: number) =>
    request<InventoryItem>("/api/inventory", post({ ...input, createdBy })),
  updateInventoryItem: (id: number, input: InventoryInput) =>
    request<InventoryItem>(`/api/inventory/${id}`, withBody("PATCH", input)),
  // Soft delete on the server: the item is hidden, not destroyed (admins can restore it).
  deleteInventoryItem: (id: number, pin: string, deletedBy?: number) =>
    request<{ ok: true }>(`/api/inventory/${id}`, withBody("DELETE", { pin, deletedBy })),
  uploadInventoryPhoto: (itemId: number, photo: Blob, thumb: Blob | null) => {
    const form = new FormData();
    form.set("photo", photo, "photo.jpg");
    if (thumb) form.set("thumb", thumb, "thumb.jpg");
    return request<{ id: number }>(`/api/inventory/${itemId}/photos`, { method: "POST", body: form });
  },
  deleteInventoryPhoto: (id: number) => request<{ ok: true }>(`/api/inventory/photos/${id}`, { method: "DELETE" }),
  inventoryPhotoUrl: (id: number, thumb = false) => `/api/inventory/photos/${id}${thumb ? "?size=thumb" : ""}`,
  /** AI suggestion from photos (already downsized); nothing is stored server-side. */
  analyzeInventoryPhotos: (images: Blob[], options: { model?: AiModel; hint?: string } = {}) => {
    const form = new FormData();
    images.forEach((img, i) => form.append("image", img, `image-${i}.${img.type === "image/webp" ? "webp" : "jpg"}`));
    if (options.model) form.append("model", options.model);
    if (options.hint?.trim()) form.append("hint", options.hint.trim());
    return request<InventorySuggestion>("/api/inventory/analyze", { method: "POST", body: form });
  },

  pushPublicKey: () => request<{ publicKey: string }>("/api/push/public-key"),
  pushSubscribe: (subscription: PushSubscriptionJSON, driverId: number | null) =>
    request<{ ok: true }>("/api/push/subscribe", post({ subscription, driverId })),
  smsHistory: () => request<SmsMessage[]>("/api/sms"),
  sendSms: (to: string, text: string, pin: string) =>
    request<{ ok: true; to: string; from: string; sid?: string; status?: string }>("/api/sms", post({ to, text, pin })),
  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: true }>("/api/push/subscribe", withBody("DELETE", { endpoint })),
};
